import Hls from "hls.js";
import type {
  StormcloudVideoPlayerConfig,
  Scte35Marker,
  Id3TagInfo,
  ImaController,
  AdBreak,
  StormcloudApiResponse,
} from "../types";
import { createImaController } from "../sdk/ima";
import { createHlsAdPlayer } from "../sdk/hlsAdPlayer";
import { sendInitialTracking, sendHeartbeat } from "../utils/tracking";
import { initializePolyfills } from "../utils/polyfills";
import {
  supportsGoogleIMA,
  logBrowserInfo,
  getBrowserConfigOverrides,
} from "../utils/browserCompat";

export class StormcloudVideoPlayer {
  private readonly video: HTMLVideoElement;
  private readonly config: StormcloudVideoPlayerConfig;
  private hls?: Hls;
  private ima: ImaController;
  private attached = false;
  private inAdBreak = false;
  private currentAdBreakStartWallClockMs: number | undefined;
  private expectedAdBreakDurationMs: number | undefined;
  private adStopTimerId: number | undefined;
  private adStartTimerId: number | undefined;
  private adFailsafeTimerId: number | undefined;
  private ptsDriftEmaMs = 0;
  private adPodQueue: string[] = [];
  private apiVastTagUrl: string | undefined;
  private apiNumberAds: number | undefined;
  private lastHeartbeatTime: number = 0;
  private heartbeatInterval: number | undefined;
  private currentAdIndex: number = 0;
  private totalAdsInBreak: number = 0;
  private showAds: boolean = false;
  private isLiveStream: boolean = false;
  private nativeHlsMode: boolean = false;
  private videoSrcProtection: string | null = null;
  private bufferedSegmentsCount: number = 0;
  private shouldAutoplayAfterBuffering: boolean = false;
  private hasInitialBufferCompleted: boolean = false;
  private adPodAllUrls: string[] = [];
  private preloadingAdUrls: Set<string> = new Set();
  private vastToMediaUrlMap: Map<string, string[]> = new Map();
  private preloadedMediaUrls: Set<string> = new Set();
  private preloadingMediaUrls: Set<string> = new Set();
  private adRequestTokenCounter = 0;
  private activeAdRequestToken: number | null = null;
  private adRequestWatchdogId: number | undefined;
  private adRequestWatchdogToken: number | null = null;
  private adFailsafeToken: number | null = null;
  private fetchedAdDurations: Map<string, number> = new Map();
  private targetAdBreakDurationMs: number | null = null;
  private isAdaptiveMode: boolean = false;
  private failedVastUrls: Set<string> = new Set();
  private continuousFetchingActive: boolean = false;
  private adRequestQueue: string[] = [];
  private successfulAdRequests: string[] = [];
  private maxPlaceholderDurationMs: number = 5000;
  private placeholderStartTimeMs: number | null = null;
  private isShowingPlaceholder: boolean = false;

  constructor(config: StormcloudVideoPlayerConfig) {
    initializePolyfills();

    const browserOverrides = getBrowserConfigOverrides();
    this.config = { ...config, ...browserOverrides };
    this.video = config.videoElement;

    logBrowserInfo(config.debugAdTiming);

    this.ima = this.createAdPlayer(false);
  }

  private createAdPlayer(continueLiveStreamDuringAds: boolean): ImaController {
    const vastMode = this.config.vastMode || "default";
    let adPlayerType =
      this.config.adPlayerType || (vastMode === "adstorm" ? "hls" : "ima");

    if (adPlayerType === "ima" && !supportsGoogleIMA()) {
      if (this.config.debugAdTiming) {
        console.warn(
          "[StormcloudVideoPlayer] Google IMA SDK not supported on this browser, falling back to HLS ad player"
        );
      }
      adPlayerType = "hls";
    }

    if (adPlayerType === "hls") {
      if (this.config.debugAdTiming) {
        console.log(
          "[StormcloudVideoPlayer] Creating HLS ad player (AdStorm mode)"
        );
      }
      return createHlsAdPlayer(this.video, {
        continueLiveStreamDuringAds,
        ...(this.config.licenseKey
          ? { licenseKey: this.config.licenseKey }
          : {}),
        ...(this.hls ? { mainHlsInstance: this.hls } : {}),
      });
    } else {
      if (this.config.debugAdTiming) {
        console.log(
          "[StormcloudVideoPlayer] Creating Google IMA ad player (Default mode)"
        );
      }
      return createImaController(this.video, {
        continueLiveStreamDuringAds,
      });
    }
  }

  async load(): Promise<void> {
    if (!this.attached) {
      this.attach();
    }

    try {
      await this.fetchAdConfiguration();
    } catch (error) {
      if (this.config.debugAdTiming) {
        console.warn(
          "[StormcloudVideoPlayer] Failed to fetch ad configuration:",
          error
        );
      }
    }

    this.initializeTracking();

    if (this.shouldUseNativeHls()) {
      this.nativeHlsMode = true;
      this.videoSrcProtection = this.config.src;
      this.video.src = this.config.src;

      this.isLiveStream = this.config.lowLatencyMode ?? false;

      if (this.config.debugAdTiming) {
        console.log(
          "[StormcloudVideoPlayer] Using native HLS playback - VOD mode:",
          {
            isLive: this.isLiveStream,
            allowNativeHls: this.config.allowNativeHls,
            adBehavior: "vod (main video pauses during ads)",
          }
        );
      }

      this.ima.destroy();
      this.ima = this.createAdPlayer(false);
      this.ima.initialize();

      if (this.config.autoplay) {
        await this.video.play()?.catch(() => {});
      }
      return;
    }

    this.hls = new Hls({
      enableWorker: true,
      backBufferLength: 30,
      liveDurationInfinity: true,
      lowLatencyMode: !!this.config.lowLatencyMode,
      maxLiveSyncPlaybackRate: this.config.lowLatencyMode ? 1.5 : 1.0,
      ...(this.config.lowLatencyMode ? { liveSyncDuration: 2 } : {}),
      maxBufferLength: 30,
      maxMaxBufferLength: 600,
      maxBufferSize: 60 * 1000 * 1000,
      maxBufferHole: 0.5,
      highBufferWatchdogPeriod: 2,
      nudgeOffset: 0.1,
      nudgeMaxRetry: 3,
      startPosition: -1,
    });

    this.hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      this.hls?.loadSource(this.config.src);
    });

    this.hls.on(Hls.Events.MANIFEST_PARSED, async (_, data: any) => {
      this.isLiveStream =
        this.hls?.levels?.some(
          (level) =>
            level?.details?.live === true || level?.details?.type === "LIVE"
        ) ?? false;

      if (this.config.debugAdTiming) {
        const adBehavior = this.shouldContinueLiveStreamDuringAds()
          ? "live (main video continues muted during ads)"
          : "vod (main video pauses during ads)";
        console.log("[StormcloudVideoPlayer] Stream type detected:", {
          isLive: this.isLiveStream,
          allowNativeHls: this.config.allowNativeHls,
          adBehavior,
        });
      }

      this.ima.destroy();
      this.ima = this.createAdPlayer(this.shouldContinueLiveStreamDuringAds());
      this.ima.initialize();

      this.bufferedSegmentsCount = 0;
      this.hasInitialBufferCompleted = false;
      this.shouldAutoplayAfterBuffering = !!this.config.autoplay;

      const minSegments = this.config.minSegmentsBeforePlay ?? 2;

      if (this.config.debugAdTiming) {
        console.log(
          "[StormcloudVideoPlayer] Waiting for",
          minSegments,
          "segments to buffer before playback"
        );
      }

      if (minSegments === 0 || !this.config.autoplay) {
        this.hasInitialBufferCompleted = true;
        if (this.config.autoplay) {
          await this.video.play()?.catch(() => {});
        }
      }
    });

    this.hls.on(Hls.Events.FRAG_BUFFERED, async (_evt, data: any) => {
      if (this.hasInitialBufferCompleted) {
        return;
      }

      this.bufferedSegmentsCount++;
      const minSegments = this.config.minSegmentsBeforePlay ?? 2;

      if (this.config.debugAdTiming) {
        console.log(
          `[StormcloudVideoPlayer] Buffered segment ${this.bufferedSegmentsCount}/${minSegments}`
        );
      }

      if (this.bufferedSegmentsCount >= minSegments) {
        this.hasInitialBufferCompleted = true;

        if (this.shouldAutoplayAfterBuffering) {
          if (this.config.debugAdTiming) {
            console.log(
              `[StormcloudVideoPlayer] Initial buffer complete (${this.bufferedSegmentsCount} segments). Starting playback.`
            );
          }
          await this.video.play()?.catch((err) => {
            if (this.config.debugAdTiming) {
              console.warn("[StormcloudVideoPlayer] Autoplay failed:", err);
            }
          });
        }
      }
    });

    this.hls.on(Hls.Events.FRAG_PARSING_METADATA, (_evt, data: any) => {
      const id3Tags: Id3TagInfo[] = (data?.samples || []).map((s: any) => ({
        key: "ID3",
        value: s?.data,
        ptsSeconds: s?.pts,
      }));
      id3Tags.forEach((tag) => this.onId3Tag(tag));
    });

    this.hls.on(Hls.Events.FRAG_CHANGED, (_evt, data: any) => {
      const frag = data?.frag;
      const tagList: any[] | undefined = frag?.tagList;
      if (!Array.isArray(tagList)) return;

      for (const entry of tagList) {
        let tag = "";
        let value = "";
        if (Array.isArray(entry)) {
          tag = String(entry[0] ?? "");
          value = String(entry[1] ?? "");
        } else if (typeof entry === "string") {
          const idx = entry.indexOf(":");
          if (idx >= 0) {
            tag = entry.substring(0, idx);
            value = entry.substring(idx + 1);
          } else {
            tag = entry;
            value = "";
          }
        }

        if (!tag) continue;
        if (tag.includes("EXT-X-CUE-OUT")) {
          const durationSeconds = this.parseCueOutDuration(value);
          const marker: Scte35Marker = {
            type: "start",
            ...(durationSeconds !== undefined ? { durationSeconds } : {}),
            raw: { tag, value },
          } as Scte35Marker;
          this.onScte35Marker(marker);
        } else if (tag.includes("EXT-X-CUE-OUT-CONT")) {
          const prog = this.parseCueOutCont(value);
          const marker: Scte35Marker = {
            type: "progress",
            ...(prog?.duration !== undefined
              ? { durationSeconds: prog.duration }
              : {}),
            ...(prog?.elapsed !== undefined
              ? { ptsSeconds: prog.elapsed }
              : {}),
            raw: { tag, value },
          } as Scte35Marker;
          this.onScte35Marker(marker);
        } else if (tag.includes("EXT-X-CUE-IN")) {
          this.onScte35Marker({ type: "end", raw: { tag, value } });
        } else if (tag.includes("EXT-X-DATERANGE")) {
          const attrs = this.parseAttributeList(value);
          const hasScteOut =
            "SCTE35-OUT" in attrs || attrs["SCTE35-OUT"] !== undefined;
          const hasScteIn =
            "SCTE35-IN" in attrs || attrs["SCTE35-IN"] !== undefined;
          const klass = String(attrs["CLASS"] ?? "");
          const duration = this.toNumber(attrs["DURATION"]);

          if (hasScteOut || /com\.apple\.hls\.cue/i.test(klass)) {
            const marker: Scte35Marker = {
              type: "start",
              ...(duration !== undefined ? { durationSeconds: duration } : {}),
              raw: { tag, value, attrs },
            } as Scte35Marker;
            this.onScte35Marker(marker);
          }
          if (hasScteIn) {
            this.onScte35Marker({ type: "end", raw: { tag, value, attrs } });
          }
        }
      }
    });

    this.hls.on(Hls.Events.ERROR, (_evt, data) => {
      if (data?.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            this.hls?.startLoad();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            this.hls?.recoverMediaError();
            break;
          default:
            this.destroy();
            break;
        }
      }
    });

    this.hls.attachMedia(this.video);
  }

  private attach(): void {
    if (this.attached) return;
    this.attached = true;
    this.video.autoplay = !!this.config.autoplay;
    this.video.muted = !!this.config.muted;

    this.ima.initialize();
    this.ima.updateOriginalMutedState(this.video.muted, this.video.volume);
    this.ima.on("all_ads_completed", () => {
      if (this.config.debugAdTiming) {
        console.log(
          "[StormcloudVideoPlayer] IMA all_ads_completed event received - ending ad break"
        );
      }

      if (this.inAdBreak) {
        this.handleAdPodComplete();
      }
    });
    this.ima.on("ad_error", (errorPayload?: any) => {
      const remaining = this.getRemainingAdMs();
      console.error("[AD-ERROR] Ad playback failed", errorPayload || "");
      
      if (this.inAdBreak) {
        if (remaining > 500 && this.adPodQueue.length > 0) {
          const nextPreloaded = this.findNextPreloadedAd();
          if (nextPreloaded) {
            this.currentAdIndex++;
            this.playSingleAd(nextPreloaded).catch(() => {
              this.handleAdFailure();
            });
          } else {
            this.handleAdFailure();
          }
        } else {
          this.handleAdFailure();
        }
      } else {
        this.handleAdFailure();
      }
    });
    this.ima.on("content_pause", () => {
      this.clearAdFailsafeTimer();
      this.clearAdRequestWatchdog();
      this.activeAdRequestToken = null;
      this.showAds = true;
    });
    this.ima.on("content_resume", () => {
      this.clearAdFailsafeTimer();
      this.clearAdRequestWatchdog();
      this.activeAdRequestToken = null;
      this.showAds = false;

      if (!this.inAdBreak) {
        return;
      }

      const remaining = this.getRemainingAdMs();
      
      if (this.config.debugAdTiming) {
        console.log(`[CONTINUOUS-FETCH] content_resume event: remaining=${remaining}ms, queued ads=${this.adRequestQueue.length}`);
      }

      if (remaining > 500) {
        this.tryNextAvailableAd();
      } else {
        this.handleAdPodComplete();
      }
    });

    this.video.addEventListener("timeupdate", () => {
      this.onTimeUpdate(this.video.currentTime);
    });

    this.video.addEventListener("emptied", () => {
      if (
        this.nativeHlsMode &&
        this.videoSrcProtection &&
        !this.ima.isAdPlaying()
      ) {
        if (this.config.debugAdTiming) {
          console.log(
            "[StormcloudVideoPlayer] Video src was cleared, restoring:",
            this.videoSrcProtection
          );
        }
        const currentTime = this.video.currentTime;
        const wasPaused = this.video.paused;
        this.video.src = this.videoSrcProtection;
        this.video.currentTime = currentTime;
        if (!wasPaused) {
          this.video.play().catch(() => {});
        }
      }
    });
  }

  private shouldUseNativeHls(): boolean {
    const streamType = this.getStreamType();

    if (streamType === "other") {
      return true;
    }

    const canNative = this.video.canPlayType("application/vnd.apple.mpegurl");
    return !!(this.config.allowNativeHls && canNative);
  }

  private onId3Tag(tag: Id3TagInfo): void {
    if (typeof tag.ptsSeconds === "number") {
      this.updatePtsDrift(tag.ptsSeconds);
    }
    const marker = this.parseScte35FromId3(tag);
    if (marker) {
      this.onScte35Marker(marker);
    }
  }

  private parseScte35FromId3(tag: Id3TagInfo): Scte35Marker | undefined {
    const text = this.decodeId3ValueToText(tag.value);
    if (!text) return undefined;

    const cueOutMatch =
      text.match(/EXT-X-CUE-OUT(?::([^\r\n]*))?/i) ||
      text.match(/CUE-OUT(?::([^\r\n]*))?/i);
    if (cueOutMatch) {
      const arg = (cueOutMatch[1] ?? "").trim();
      const dur = this.parseCueOutDuration(arg);
      const marker: Scte35Marker = {
        type: "start",
        ...(tag.ptsSeconds !== undefined ? { ptsSeconds: tag.ptsSeconds } : {}),
        ...(dur !== undefined ? { durationSeconds: dur } : {}),
        raw: { id3: text },
      } as Scte35Marker;
      return marker;
    }

    const cueOutContMatch = text.match(/EXT-X-CUE-OUT-CONT:([^\r\n]*)/i);
    if (cueOutContMatch) {
      const arg = (cueOutContMatch[1] ?? "").trim();
      const cont = this.parseCueOutCont(arg);
      const marker: Scte35Marker = {
        type: "progress",
        ...(tag.ptsSeconds !== undefined ? { ptsSeconds: tag.ptsSeconds } : {}),
        ...(cont?.duration !== undefined
          ? { durationSeconds: cont.duration }
          : {}),
        raw: { id3: text },
      } as Scte35Marker;
      return marker;
    }

    const cueInMatch = text.match(/EXT-X-CUE-IN\b/i) || text.match(/CUE-IN\b/i);
    if (cueInMatch) {
      const marker: Scte35Marker = {
        type: "end",
        ...(tag.ptsSeconds !== undefined ? { ptsSeconds: tag.ptsSeconds } : {}),
        raw: { id3: text },
      } as Scte35Marker;
      return marker;
    }

    const daterangeMatch = text.match(/EXT-X-DATERANGE:([^\r\n]*)/i);
    if (daterangeMatch) {
      const attrs = this.parseAttributeList(daterangeMatch[1] ?? "");
      const hasScteOut =
        "SCTE35-OUT" in attrs || attrs["SCTE35-OUT"] !== undefined;
      const hasScteIn =
        "SCTE35-IN" in attrs || attrs["SCTE35-IN"] !== undefined;
      const klass = String(attrs["CLASS"] ?? "");
      const duration = this.toNumber(attrs["DURATION"]);
      if (hasScteOut || /com\.apple\.hls\.cue/i.test(klass)) {
        const marker: Scte35Marker = {
          type: "start",
          ...(tag.ptsSeconds !== undefined
            ? { ptsSeconds: tag.ptsSeconds }
            : {}),
          ...(duration !== undefined ? { durationSeconds: duration } : {}),
          raw: { id3: text, attrs },
        } as Scte35Marker;
        return marker;
      }
      if (hasScteIn) {
        const marker: Scte35Marker = {
          type: "end",
          ...(tag.ptsSeconds !== undefined
            ? { ptsSeconds: tag.ptsSeconds }
            : {}),
          raw: { id3: text, attrs },
        } as Scte35Marker;
        return marker;
      }
    }

    if (/SCTE35-OUT/i.test(text)) {
      const marker: Scte35Marker = {
        type: "start",
        ...(tag.ptsSeconds !== undefined ? { ptsSeconds: tag.ptsSeconds } : {}),
        raw: { id3: text },
      } as Scte35Marker;
      return marker;
    }
    if (/SCTE35-IN/i.test(text)) {
      const marker: Scte35Marker = {
        type: "end",
        ...(tag.ptsSeconds !== undefined ? { ptsSeconds: tag.ptsSeconds } : {}),
        raw: { id3: text },
      } as Scte35Marker;
      return marker;
    }

    if (tag.value instanceof Uint8Array) {
      const bin = this.parseScte35Binary(tag.value);
      if (bin) return bin;
    }

    return undefined;
  }

  private decodeId3ValueToText(value: string | Uint8Array): string | undefined {
    try {
      if (typeof value === "string") return value;
      const decoder = new TextDecoder("utf-8", { fatal: false });
      const text = decoder.decode(value);
      if (text && /[\x20-\x7E]/.test(text)) return text;
      let out = "";
      for (let i = 0; i < value.length; i++)
        out += String.fromCharCode(value[i]!);
      return out;
    } catch {
      return undefined;
    }
  }

  private onScte35Marker(marker: Scte35Marker): void {
    if (this.config.debugAdTiming) {
      console.log("[StormcloudVideoPlayer] SCTE-35 marker detected:", {
        type: marker.type,
        ptsSeconds: marker.ptsSeconds,
        durationSeconds: marker.durationSeconds,
        currentTime: this.video.currentTime,
        raw: marker.raw,
      });
    }

    if (marker.type === "start") {
      if (this.inAdBreak) {
        return;
      }

      this.inAdBreak = true;
      const durationMs =
        marker.durationSeconds != null
          ? marker.durationSeconds * 1000
          : undefined;
      this.expectedAdBreakDurationMs = durationMs;
      this.currentAdBreakStartWallClockMs = Date.now();

      const isManifestMarker = this.isManifestBasedMarker(marker);
      const forceImmediate = this.config.immediateManifestAds ?? true;

      if (this.config.debugAdTiming) {
        console.log("[StormcloudVideoPlayer] Ad start decision:", {
          isManifestMarker,
          forceImmediate,
          hasPts: typeof marker.ptsSeconds === "number",
        });
      }

      if (isManifestMarker && forceImmediate) {
        if (this.config.debugAdTiming) {
          console.log(
            "[StormcloudVideoPlayer] Starting ad immediately (manifest-based)"
          );
        }
        this.clearAdStartTimer();
        this.handleAdStart(marker);
      } else if (typeof marker.ptsSeconds === "number") {
        const tol = this.config.driftToleranceMs ?? 1000;
        const nowMs = this.video.currentTime * 1000;
        const estCurrentPtsMs = nowMs - this.ptsDriftEmaMs;
        const deltaMs = Math.floor(marker.ptsSeconds * 1000 - estCurrentPtsMs);

        if (this.config.debugAdTiming) {
          console.log("[StormcloudVideoPlayer] PTS-based timing calculation:", {
            nowMs,
            estCurrentPtsMs,
            markerPtsMs: marker.ptsSeconds * 1000,
            deltaMs,
            tolerance: tol,
          });
        }

        if (deltaMs > tol) {
          if (this.config.debugAdTiming) {
            console.log(
              `[StormcloudVideoPlayer] Scheduling ad start in ${deltaMs}ms`
            );
          }
          this.scheduleAdStartIn(deltaMs);
        } else {
          if (this.config.debugAdTiming) {
            console.log(
              "[StormcloudVideoPlayer] Starting ad immediately (within tolerance)"
            );
          }
          this.clearAdStartTimer();
          this.handleAdStart(marker);
        }
      } else {
        if (this.config.debugAdTiming) {
          console.log(
            "[StormcloudVideoPlayer] Starting ad immediately (fallback)"
          );
        }
        this.clearAdStartTimer();
        this.handleAdStart(marker);
      }
      if (this.expectedAdBreakDurationMs != null) {
        this.scheduleAdStopCountdown(this.expectedAdBreakDurationMs);
      }
      return;
    }
    if (marker.type === "progress" && this.inAdBreak) {
      if (marker.durationSeconds != null) {
        this.expectedAdBreakDurationMs = marker.durationSeconds * 1000;
      }
      if (
        this.expectedAdBreakDurationMs != null &&
        this.currentAdBreakStartWallClockMs != null
      ) {
        const elapsedMs = Date.now() - this.currentAdBreakStartWallClockMs;
        const remainingMs = Math.max(
          0,
          this.expectedAdBreakDurationMs - elapsedMs
        );
        this.scheduleAdStopCountdown(remainingMs);
      }
      
      if (!this.ima.isAdPlaying() && this.activeAdRequestToken === null) {
        const scheduled = this.findCurrentOrNextBreak(
          this.video.currentTime * 1000
        );
        const tags =
          this.selectVastTagsForBreak(scheduled) ||
          (this.apiVastTagUrl ? [this.apiVastTagUrl] : undefined);
        if (tags && tags.length > 0) {
          const first = tags[0] as string;
          const rest = tags.slice(1);
          this.adPodQueue = rest;
          
          this.playSingleAd(first).catch(() => {});
        }
      }
      return;
    }
    if (marker.type === "end") {
      this.inAdBreak = false;
      this.expectedAdBreakDurationMs = undefined;
      this.currentAdBreakStartWallClockMs = undefined;
      this.clearAdStartTimer();
      this.clearAdStopTimer();
      
      if (this.ima.isAdPlaying()) {
        this.ima.stop().catch(() => {});
      }
      
      this.handleAdPodComplete();
      return;
    }
  }

  private parseCueOutDuration(value: string): number | undefined {
    const num = parseFloat(value.trim());
    if (!Number.isNaN(num)) return num;
    const match =
      value.match(/(?:^|[,\s])DURATION\s*=\s*([0-9.]+)/i) ||
      value.match(/Duration\s*=\s*([0-9.]+)/i);
    if (match && match[1] != null) {
      const dStr = match[1];
      const d = parseFloat(dStr);
      return Number.isNaN(d) ? undefined : d;
    }
    return undefined;
  }

  private parseCueOutCont(
    value: string
  ): { elapsed?: number; duration?: number } | undefined {
    const elapsedMatch = value.match(/Elapsed\s*=\s*([0-9.]+)/i);
    const durationMatch = value.match(/Duration\s*=\s*([0-9.]+)/i);
    const res: { elapsed?: number; duration?: number } = {};
    if (elapsedMatch && elapsedMatch[1] != null) {
      const e = parseFloat(elapsedMatch[1]);
      if (!Number.isNaN(e)) res.elapsed = e;
    }
    if (durationMatch && durationMatch[1] != null) {
      const d = parseFloat(durationMatch[1]);
      if (!Number.isNaN(d)) res.duration = d;
    }
    if ("elapsed" in res || "duration" in res) return res;
    return undefined;
  }

  private parseAttributeList(value: string): Record<string, string> {
    const attrs: Record<string, string> = {};
    const regex = /([A-Z0-9-]+)=(("[^"]*")|([^",]*))(?:,|$)/gi;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(value)) !== null) {
      const key: string = (match[1] ?? "") as string;
      let rawVal: string = (match[3] ?? match[4] ?? "") as string;
      if (rawVal.startsWith('"') && rawVal.endsWith('"')) {
        rawVal = rawVal.slice(1, -1);
      }
      if (key) {
        attrs[key] = rawVal;
      }
    }
    return attrs;
  }

  private toNumber(val: unknown): number | undefined {
    if (val == null) return undefined;
    const n = typeof val === "string" ? parseFloat(val) : Number(val);
    return Number.isNaN(n) ? undefined : n;
  }

  private isManifestBasedMarker(marker: Scte35Marker): boolean {
    const raw = marker.raw as any;
    if (!raw) return false;

    if (raw.tag) {
      const tag = String(raw.tag);
      return (
        tag.includes("EXT-X-CUE-OUT") ||
        tag.includes("EXT-X-CUE-IN") ||
        tag.includes("EXT-X-DATERANGE")
      );
    }

    if (raw.id3) return false;

    if (raw.splice_command_type) return false;

    return false;
  }

  private parseScte35Binary(data: Uint8Array): Scte35Marker | undefined {
    class BitReader {
      private bytePos = 0;
      private bitPos = 0;
      constructor(private readonly buf: Uint8Array) {}
      readBits(numBits: number): number {
        let result = 0;
        while (numBits > 0) {
          if (this.bytePos >= this.buf.length) return result;
          const remainingInByte = 8 - this.bitPos;
          const toRead = Math.min(numBits, remainingInByte);
          const currentByte = this.buf[this.bytePos]!;
          const shift = remainingInByte - toRead;
          const mask = ((1 << toRead) - 1) & 0xff;
          const bits = (currentByte >> shift) & mask;
          result = (result << toRead) | bits;
          this.bitPos += toRead;
          if (this.bitPos >= 8) {
            this.bitPos = 0;
            this.bytePos += 1;
          }
          numBits -= toRead;
        }
        return result >>> 0;
      }
      skipBits(n: number): void {
        this.readBits(n);
      }
    }

    const r = new BitReader(data);
    const tableId = r.readBits(8);
    if (tableId !== 0xfc) return undefined;
    r.readBits(1);
    r.readBits(1);
    r.readBits(2);
    const sectionLength = r.readBits(12);
    r.readBits(8);
    r.readBits(1);
    r.readBits(6);
    const ptsAdjHigh = r.readBits(1);
    const ptsAdjLow = r.readBits(32);
    void ptsAdjHigh;
    void ptsAdjLow;
    r.readBits(8);
    r.readBits(12);
    const spliceCommandLength = r.readBits(12);
    const spliceCommandType = r.readBits(8);
    if (spliceCommandType !== 5) {
      return undefined;
    }
    r.readBits(32);
    const cancel = r.readBits(1) === 1;
    r.readBits(7);
    if (cancel) return undefined;
    const outOfNetwork = r.readBits(1) === 1;
    const programSpliceFlag = r.readBits(1) === 1;
    const durationFlag = r.readBits(1) === 1;
    const spliceImmediateFlag = r.readBits(1) === 1;
    r.readBits(4);
    if (programSpliceFlag && !spliceImmediateFlag) {
      const timeSpecifiedFlag = r.readBits(1) === 1;
      if (timeSpecifiedFlag) {
        r.readBits(6);
        r.readBits(33);
      } else {
        r.readBits(7);
      }
    } else if (!programSpliceFlag) {
      const componentCount = r.readBits(8);
      for (let i = 0; i < componentCount; i++) {
        r.readBits(8);
        if (!spliceImmediateFlag) {
          const timeSpecifiedFlag = r.readBits(1) === 1;
          if (timeSpecifiedFlag) {
            r.readBits(6);
            r.readBits(33);
          } else {
            r.readBits(7);
          }
        }
      }
    }
    let durationSeconds: number | undefined = undefined;
    if (durationFlag) {
      r.readBits(6);
      r.readBits(1);
      const high = r.readBits(1);
      const low = r.readBits(32);
      const durationTicks = high * 0x100000000 + low;
      durationSeconds = durationTicks / 90000;
    }
    r.readBits(16);
    r.readBits(8);
    r.readBits(8);

    if (outOfNetwork) {
      const marker: Scte35Marker = {
        type: "start",
        ...(durationSeconds !== undefined ? { durationSeconds } : {}),
        raw: { splice_command_type: 5 },
      } as Scte35Marker;
      return marker;
    }
    return undefined;
  }

  private initializeTracking(): void {
    sendInitialTracking(this.config.licenseKey)
      .then(() => {
        this.heartbeatInterval = window.setInterval(() => {
          this.sendHeartbeatIfNeeded();
        }, 5000);
      })
      .catch((error) => {
        if (this.config.debugAdTiming) {
          console.warn(
            "[StormcloudVideoPlayer] Failed to send initial tracking:",
            error
          );
        }
        this.heartbeatInterval = window.setInterval(() => {
          this.sendHeartbeatIfNeeded();
        }, 5000);
      });
  }

  private sendHeartbeatIfNeeded(): void {
    const now = Date.now();
    if (!this.lastHeartbeatTime || now - this.lastHeartbeatTime > 30000) {
      this.lastHeartbeatTime = now;
      sendHeartbeat(this.config.licenseKey).catch((error) => {
        if (this.config.debugAdTiming) {
          console.warn(
            "[StormcloudVideoPlayer] Failed to send heartbeat:",
            error
          );
        }
      });
    }
  }

  private async fetchAdConfiguration(): Promise<void> {
    const vastMode = this.config.vastMode || "default";

    if (this.config.debugAdTiming) {
      console.log("[StormcloudVideoPlayer] VAST mode:", vastMode);
    }

    if (vastMode === "adstorm") {
      if (!this.config.licenseKey) {
        if (this.config.debugAdTiming) {
          console.warn(
            "[StormcloudVideoPlayer] AdStorm mode requires a license key"
          );
        }
        return;
      }

      const vastEndpoint = `https://adstorm.co/api-adstorm-dev/adstorm/vast/${this.config.licenseKey}`;
      this.apiVastTagUrl = vastEndpoint;

      if (this.config.debugAdTiming) {
        console.log(
          "[StormcloudVideoPlayer] Using AdStorm VAST endpoint (adstorm mode):",
          vastEndpoint
        );
      }
      return;
    }

    if (this.config.vastTagUrl) {
      this.apiVastTagUrl = this.config.vastTagUrl;
      if (this.config.debugAdTiming) {
        console.log(
          "[StormcloudVideoPlayer] Using custom VAST tag URL:",
          this.apiVastTagUrl
        );
      }
      return;
    }

    const apiUrl = "https://adstorm.co/api-adstorm-dev/adstorm/ads/web";

    if (this.config.debugAdTiming) {
      console.log(
        "[StormcloudVideoPlayer] Fetching ad configuration from:",
        apiUrl
      );
    }

    const headers: Record<string, string> = {};
    if (this.config.licenseKey) {
      headers["Authorization"] = `Bearer ${this.config.licenseKey}`;
    }

    const response = await fetch(apiUrl, { headers });
    if (!response.ok) {
      if (this.config.debugAdTiming) {
        console.warn(
          `[StormcloudVideoPlayer] Failed to fetch ad configuration: ${response.status}`
        );
      }
      return;
    }

    const data: StormcloudApiResponse = await response.json();

    const imaPayload = data.response?.ima?.["publisherdesk.ima"]?.payload;
    if (imaPayload) {
      this.apiVastTagUrl = decodeURIComponent(imaPayload);
      if (this.config.debugAdTiming) {
        console.log(
          "[StormcloudVideoPlayer] Extracted VAST tag URL from /ads/web:",
          this.apiVastTagUrl
        );
      }
    } else {
      if (this.config.debugAdTiming) {
        console.warn(
          "[StormcloudVideoPlayer] No VAST tag URL found in /ads/web response"
        );
      }
    }

    const numberAds = data.response?.options?.vast?.cue_tones?.number_ads;
    if (numberAds != null && numberAds > 0) {
      this.apiNumberAds = numberAds;
      if (this.config.debugAdTiming) {
        console.log(
          "[StormcloudVideoPlayer] Number of ads per break from API:",
          this.apiNumberAds
        );
      }
    }
  }

  getCurrentAdIndex(): number {
    return this.currentAdIndex;
  }

  getTotalAdsInBreak(): number {
    return this.totalAdsInBreak;
  }

  private generateVastUrlsWithCorrelators(
    baseUrl: string,
    count: number
  ): string[] {
    const urls: string[] = [];
    
    for (let i = 0; i < count; i++) {
      try {
        const url = new URL(baseUrl);
        const timestamp = Date.now();
        const random = Math.floor(Math.random() * 1000000);
        const uniqueCorrelator = `${timestamp}${random}${i}`;
        
        url.searchParams.set("correlator", uniqueCorrelator);
        
        urls.push(url.toString());
      } catch (error) {
        console.warn(
          "[StormcloudVideoPlayer] Failed to parse VAST URL:",
          baseUrl,
          error
        );
        urls.push(`${baseUrl}${baseUrl.includes("?") ? "&" : "?"}correlator=${Date.now()}${i}`);
      }
    }
    
    return urls;
  }

  isAdPlaying(): boolean {
    return this.inAdBreak && this.ima.isAdPlaying();
  }

  isShowingAds(): boolean {
    return this.showAds;
  }

  getStreamType(): "hls" | "other" {
    const url = this.config.src.toLowerCase();
    if (
      url.includes(".m3u8") ||
      url.includes("/hls/") ||
      url.includes("application/vnd.apple.mpegurl")
    ) {
      return "hls";
    }
    return "other";
  }

  shouldShowNativeControls(): boolean {
    const streamType = this.getStreamType();
    if (streamType === "other") {
      return !(this.config.showCustomControls ?? false);
    }
    return !!(
      this.config.allowNativeHls && !(this.config.showCustomControls ?? false)
    );
  }

  private shouldContinueLiveStreamDuringAds(): boolean {
    if (this.config.allowNativeHls) {
      return false;
    }

    if (!this.isLiveStream) {
      return false;
    }

    return true;
  }

  private async handleAdStart(_marker: Scte35Marker): Promise<void> {
    const scheduled = this.findCurrentOrNextBreak(
      this.video.currentTime * 1000
    );
    const tags = this.selectVastTagsForBreak(scheduled);

    let baseVastUrl: string;

    if (this.apiVastTagUrl) {
      baseVastUrl = this.apiVastTagUrl;
    } else if (tags && tags.length > 0 && tags[0]) {
      baseVastUrl = tags[0];
    } else {
      return;
    }

    const adBreakDurationMs = 
      _marker.durationSeconds != null 
        ? _marker.durationSeconds * 1000 
        : scheduled?.durationMs;

    if (this.isLiveStream && adBreakDurationMs != null && adBreakDurationMs > 0) {
      this.isAdaptiveMode = true;
      this.targetAdBreakDurationMs = adBreakDurationMs;
      this.fetchedAdDurations.clear();

      if (this.config.debugAdTiming) {
        console.log(
          `[CONTINUOUS-FETCH] 📺 LIVE MODE: Target duration=${adBreakDurationMs}ms | ` +
          `Will continuously fetch ads during break`
        );
      }
    } else {
      this.isAdaptiveMode = false;
      this.targetAdBreakDurationMs = null;
      this.fetchedAdDurations.clear();

      if (this.config.debugAdTiming) {
        console.log(
          `[CONTINUOUS-FETCH] 🎬 VOD MODE: Using fixed ad strategy`
        );
      }
    }

    this.adPodAllUrls = [];
    this.preloadingAdUrls.clear();
    this.vastToMediaUrlMap.clear();
    this.preloadedMediaUrls.clear();
    this.preloadingMediaUrls.clear();
    this.failedVastUrls.clear();
    this.adRequestQueue = [];
    this.successfulAdRequests = [];
    this.continuousFetchingActive = true;
    this.isShowingPlaceholder = false;
    this.placeholderStartTimeMs = null;

    const currentMuted = this.video.muted;
    const currentVolume = this.video.volume;
    this.ima.updateOriginalMutedState(currentMuted, currentVolume);

    this.inAdBreak = true;
    this.currentAdIndex = 0;
    this.totalAdsInBreak = 1;
    this.adPodQueue = [];

    if (
      this.expectedAdBreakDurationMs == null &&
      adBreakDurationMs != null
    ) {
      this.expectedAdBreakDurationMs = adBreakDurationMs;
      this.currentAdBreakStartWallClockMs = Date.now();
      this.scheduleAdStopCountdown(this.expectedAdBreakDurationMs);
    }

    if (this.config.debugAdTiming) {
      console.log("[CONTINUOUS-FETCH] 🚀 Immediately requesting first ad...");
    }

    const firstAdUrlArray = this.generateVastUrlsWithCorrelators(baseVastUrl, 1);
    const firstAdUrl = firstAdUrlArray[0];
    
    if (!firstAdUrl) {
      if (this.config.debugAdTiming) {
        console.warn("[CONTINUOUS-FETCH] ⚠️ Failed to generate first ad URL");
      }
      this.handleAdPodComplete();
      return;
    }

    try {
      await this.ima.requestAds(firstAdUrl);
      
      if (this.config.debugAdTiming) {
        console.log("[CONTINUOUS-FETCH] ✅ First ad request successful, starting playback");
      }

      this.successfulAdRequests.push(firstAdUrl);
      this.currentAdIndex++;
      
      this.startContinuousFetching(baseVastUrl);
      
      await this.ima.play();
    } catch (error) {
      if (this.config.debugAdTiming) {
        console.warn("[CONTINUOUS-FETCH] ⚠️ First ad request failed:", error);
      }
      this.failedVastUrls.add(firstAdUrl);
      
      this.startContinuousFetching(baseVastUrl);
      this.tryNextAvailableAd();
    }
  }

  private startContinuousFetching(baseVastUrl: string): void {
    if (!this.continuousFetchingActive) {
      return;
    }

    if (this.config.debugAdTiming) {
      console.log("[CONTINUOUS-FETCH] 🔄 Starting continuous ad fetching loop");
    }

    this.continuousFetchLoop(baseVastUrl);
  }

  private async continuousFetchLoop(baseVastUrl: string): Promise<void> {
    while (this.continuousFetchingActive && this.inAdBreak) {
      const remaining = this.getRemainingAdMs();
      
      if (remaining <= 0) {
        if (this.config.debugAdTiming) {
          console.log("[CONTINUOUS-FETCH] ⏹️ Ad break time expired, stopping fetch loop");
        }
        break;
      }

      const maxQueueSize = 3;
      if (this.adRequestQueue.length >= maxQueueSize) {
        if (this.config.debugAdTiming) {
          console.log(`[CONTINUOUS-FETCH] ⏸️ Queue full (${this.adRequestQueue.length}), pausing fetching...`);
        }
        await new Promise(resolve => setTimeout(resolve, 2000));
        continue;
      }

      const newAdUrl = this.generateVastUrlsWithCorrelators(baseVastUrl, 1)[0];
      
      if (!newAdUrl || this.failedVastUrls.has(newAdUrl)) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        continue;
      }

      if (this.config.debugAdTiming) {
        console.log(`[CONTINUOUS-FETCH] 📡 Attempting to fetch ad (${this.successfulAdRequests.length + this.adRequestQueue.length + 1} total)...`);
      }

      try {
        const response = await fetch(newAdUrl, { mode: "cors" });
        if (!response.ok) {
          throw new Error(`Failed to fetch VAST: ${response.status}`);
        }
        
        const xmlText = await response.text();
        
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, "text/xml");
        const mediaFiles = xmlDoc.querySelectorAll("MediaFile");
        
        if (mediaFiles.length === 0) {
          if (this.config.debugAdTiming) {
            console.log("[CONTINUOUS-FETCH] ⚠️ VAST response has no media files, skipping");
          }
          this.failedVastUrls.add(newAdUrl);
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }

        if (this.config.debugAdTiming) {
          console.log(`[CONTINUOUS-FETCH] ✅ Successfully fetched ad, adding to queue (queue size: ${this.adRequestQueue.length + 1})`);
        }
        
        this.adRequestQueue.push(newAdUrl);
        this.totalAdsInBreak++;
        
        await new Promise(resolve => setTimeout(resolve, 500));
        
      } catch (error) {
        if (this.config.debugAdTiming) {
          console.log("[CONTINUOUS-FETCH] ❌ Ad fetch failed:", (error as Error).message);
        }
        this.failedVastUrls.add(newAdUrl);
        
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }

    if (this.config.debugAdTiming) {
      console.log("[CONTINUOUS-FETCH] 🛑 Continuous fetch loop ended");
    }
  }

  private stopContinuousFetching(): void {
    this.continuousFetchingActive = false;
    
    if (this.config.debugAdTiming) {
      console.log("[CONTINUOUS-FETCH] 🛑 Stopping continuous ad fetching");
    }
  }

  private async tryNextAvailableAd(retryCount: number = 0): Promise<void> {
    const remaining = this.getRemainingAdMs();
    
    if (remaining <= 500) {
      if (this.config.debugAdTiming) {
        console.log("[CONTINUOUS-FETCH] ⏹️ No time remaining, ending ad break");
      }
      this.handleAdPodComplete();
      return;
    }

    if (this.adRequestQueue.length > 0) {
      const nextAdUrl = this.adRequestQueue.shift();
      if (nextAdUrl) {
        if (this.config.debugAdTiming) {
          console.log(`[CONTINUOUS-FETCH] 🎬 Playing next queued ad (${this.currentAdIndex + 1}/${this.totalAdsInBreak}, ${this.adRequestQueue.length} remaining in queue)`);
        }
        
        const currentMuted = this.video.muted;
        const currentVolume = this.video.volume;
        this.ima.updateOriginalMutedState(currentMuted, currentVolume);
        
        if (this.config.debugAdTiming) {
          console.log(`[CONTINUOUS-FETCH] 🔊 Updated ad audio state: muted=${currentMuted}, volume=${currentVolume}`);
        }
        
        this.currentAdIndex++;
        this.successfulAdRequests.push(nextAdUrl);
        await this.playSingleAd(nextAdUrl).catch(() => {
          this.tryNextAvailableAd(0);
        });
        return;
      }
    }

    const maxRetries = 5;
    if (this.continuousFetchingActive && retryCount < maxRetries && remaining > 2000) {
      if (this.config.debugAdTiming) {
        console.log(`[CONTINUOUS-FETCH] ⏳ Queue empty but fetching active, waiting... (retry ${retryCount + 1}/${maxRetries})`);
      }
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      await this.tryNextAvailableAd(retryCount + 1);
      return;
    }

    if (!this.isShowingPlaceholder && remaining > 1000) {
      this.showPlaceholderAndWaitForAds();
    } else {
      if (this.config.debugAdTiming) {
        console.log("[CONTINUOUS-FETCH] ⏹️ No more ads available, ending ad break");
      }
      this.handleAdPodComplete();
    }
  }

  private async showPlaceholderAndWaitForAds(): Promise<void> {
    const remaining = this.getRemainingAdMs();
    const waitTime = Math.min(this.maxPlaceholderDurationMs, remaining);
    
    if (waitTime < 1000) {
      this.handleAdPodComplete();
      return;
    }

    if (this.config.debugAdTiming) {
      console.log(`[CONTINUOUS-FETCH] ⬛ Showing black placeholder for ${waitTime}ms while waiting for ads`);
    }

    this.isShowingPlaceholder = true;
    this.placeholderStartTimeMs = Date.now();
    
    this.ima.showPlaceholder();

    const checkInterval = 500;
    const maxChecks = Math.floor(waitTime / checkInterval);
    
    for (let i = 0; i < maxChecks; i++) {
      await new Promise(resolve => setTimeout(resolve, checkInterval));
      
      if (!this.inAdBreak) {
        return;
      }

      if (this.adRequestQueue.length > 0) {
        if (this.config.debugAdTiming) {
          console.log("[CONTINUOUS-FETCH] ✅ New ad became available during placeholder");
        }
        
        this.isShowingPlaceholder = false;
        this.placeholderStartTimeMs = null;
        this.ima.hidePlaceholder();
        
        const currentMuted = this.video.muted;
        const currentVolume = this.video.volume;
        this.ima.updateOriginalMutedState(currentMuted, currentVolume);
        
        const nextAdUrl = this.adRequestQueue.shift();
        if (nextAdUrl) {
          this.currentAdIndex++;
          this.successfulAdRequests.push(nextAdUrl);
          await this.playSingleAd(nextAdUrl).catch(() => {
            this.tryNextAvailableAd();
          });
        }
        return;
      }
    }

    if (this.config.debugAdTiming) {
      console.log("[CONTINUOUS-FETCH] ⏰ Placeholder timeout reached, no ads fetched");
    }
    
    this.isShowingPlaceholder = false;
    this.placeholderStartTimeMs = null;
    this.ima.hidePlaceholder();
    this.handleAdPodComplete();
  }

  private findCurrentOrNextBreak(nowMs: number): AdBreak | undefined {
    const schedule: AdBreak[] = [];
    let candidate: AdBreak | undefined;
    for (const b of schedule) {
      const tol = this.config.driftToleranceMs ?? 1000;
      if (
        b.startTimeMs <= nowMs + tol &&
        (candidate == null || b.startTimeMs > (candidate.startTimeMs || 0))
      ) {
        candidate = b;
      }
    }
    return candidate;
  }

  private onTimeUpdate(currentTimeSec: number): void {
    if (this.ima.isAdPlaying()) return;
    const nowMs = currentTimeSec * 1000;
    const breakToPlay = this.findBreakForTime(nowMs);
    if (breakToPlay) {
      this.handleMidAdJoin(breakToPlay, nowMs);
    }
  }

  private async handleMidAdJoin(
    adBreak: AdBreak,
    nowMs: number
  ): Promise<void> {
    const durationMs = adBreak.durationMs ?? 0;
    const endMs = adBreak.startTimeMs + durationMs;
    if (durationMs > 0 && nowMs > adBreak.startTimeMs && nowMs < endMs) {
      const remainingMs = endMs - nowMs;
      const tags =
        this.selectVastTagsForBreak(adBreak) ||
        (this.apiVastTagUrl ? [this.apiVastTagUrl] : undefined);
      if (tags && tags.length > 0) {
        const first = tags[0] as string;
        const rest = tags.slice(1);
        this.adPodQueue = rest;
        
        this.ima.updateOriginalMutedState(this.video.muted, this.video.volume);
        
        await this.playSingleAd(first);
        this.inAdBreak = true;
        this.expectedAdBreakDurationMs = remainingMs;
        this.currentAdBreakStartWallClockMs = Date.now();
        this.scheduleAdStopCountdown(remainingMs);
      }
    }
  }

  private scheduleAdStopCountdown(remainingMs: number): void {
    this.clearAdStopTimer();
    const ms = Math.max(0, Math.floor(remainingMs));
    if (ms === 0) {
      this.ensureAdStoppedByTimer();
      return;
    }
    this.adStopTimerId = window.setTimeout(() => {
      this.ensureAdStoppedByTimer();
    }, ms) as unknown as number;
  }

  private clearAdStopTimer(): void {
    if (this.adStopTimerId != null) {
      clearTimeout(this.adStopTimerId);
      this.adStopTimerId = undefined;
    }
  }

  private ensureAdStoppedByTimer(): void {
    if (!this.inAdBreak) return;

    this.adStopTimerId = undefined;

    const adPlaying = this.ima.isAdPlaying();
    const pendingAds = this.adPodQueue.length > 0;
    const checkIntervalMs = Math.max(
      250,
      Math.floor(this.config.adBreakCheckIntervalMs ?? 1000)
    );
    const maxExtensionMsConfig = this.config.maxAdBreakExtensionMs;
    const maxExtensionMs =
      typeof maxExtensionMsConfig === "number" && maxExtensionMsConfig > 0
        ? maxExtensionMsConfig
        : 60000;

    let elapsedSinceStartMs = 0;
    if (this.currentAdBreakStartWallClockMs != null) {
      elapsedSinceStartMs = Date.now() - this.currentAdBreakStartWallClockMs;
    }
    const expectedDurationMs = this.expectedAdBreakDurationMs ?? 0;
    const overrunMs = Math.max(0, elapsedSinceStartMs - expectedDurationMs);

    const shouldExtendAdBreak =
      (adPlaying || pendingAds || this.showAds) && overrunMs < maxExtensionMs;

    if (shouldExtendAdBreak) {
      this.scheduleAdStopCountdown(checkIntervalMs);
      return;
    }

    if (adPlaying) {
      this.ima.stop().catch(() => {});
    }

    this.handleAdPodComplete();
  }

  private scheduleAdStartIn(delayMs: number): void {
    this.clearAdStartTimer();
    const ms = Math.max(0, Math.floor(delayMs));
    if (ms === 0) {
      this.handleAdStart({ type: "start" } as Scte35Marker).catch(() => {});
      return;
    }
    this.adStartTimerId = window.setTimeout(() => {
      this.handleAdStart({ type: "start" } as Scte35Marker).catch(() => {});
    }, ms) as unknown as number;
  }

  private clearAdStartTimer(): void {
    if (this.adStartTimerId != null) {
      clearTimeout(this.adStartTimerId);
      this.adStartTimerId = undefined;
    }
  }

  private updatePtsDrift(ptsSecondsSample: number): void {
    const sampleMs = (this.video.currentTime - ptsSecondsSample) * 1000;
    if (!Number.isFinite(sampleMs) || Math.abs(sampleMs) > 60000) return;
    const alpha = 0.1;
    this.ptsDriftEmaMs = this.ptsDriftEmaMs * (1 - alpha) + sampleMs * alpha;
  }

  private async playSingleAd(vastTagUrl: string): Promise<void> {
    if (this.ima.isAdPlaying()) {
      return;
    }

    if (this.failedVastUrls.has(vastTagUrl)) {
      console.warn("[AD-ERROR] Skipping already-failed VAST URL:", vastTagUrl.substring(0, 60));
      this.handleAdFailure();
      return;
    }

    const requestToken = ++this.adRequestTokenCounter;
    this.activeAdRequestToken = requestToken;
    
    this.startAdRequestWatchdog(requestToken);

    try {
      await this.ima.requestAds(vastTagUrl);

      this.clearAdRequestWatchdog();

      if (this.activeAdRequestToken !== requestToken) {
        return;
      }

      try {
        this.startAdFailsafeTimer(requestToken);
        await this.ima.play();
      } catch (playError) {
        console.error("[AD-ERROR] Failed to play ad:", playError);
        this.failedVastUrls.add(vastTagUrl);
        this.clearAdFailsafeTimer();
        if (this.activeAdRequestToken === requestToken) {
          this.activeAdRequestToken = null;
        }
        this.handleAdFailure();
        return;
      }
    } catch (error) {
      console.error("[AD-ERROR] Ad request failed:", (error as Error)?.message);
      this.failedVastUrls.add(vastTagUrl);

      this.clearAdRequestWatchdog();
      this.clearAdFailsafeTimer();
      if (this.activeAdRequestToken === requestToken) {
        this.activeAdRequestToken = null;
      }

      this.handleAdFailure();
    }
  }

  private handleAdPodComplete(): void {
    this.clearAdRequestWatchdog();
    this.clearAdFailsafeTimer();
    this.activeAdRequestToken = null;

    this.stopContinuousFetching();
    
    if (this.isShowingPlaceholder) {
      this.ima.hidePlaceholder();
      this.isShowingPlaceholder = false;
      this.placeholderStartTimeMs = null;
    }

    this.preloadingAdUrls.clear();
    this.vastToMediaUrlMap.clear();
    this.preloadedMediaUrls.clear();
    this.preloadingMediaUrls.clear();
    this.adRequestQueue = [];
    this.successfulAdRequests = [];

    this.inAdBreak = false;
    this.expectedAdBreakDurationMs = undefined;
    this.currentAdBreakStartWallClockMs = undefined;
    this.clearAdStartTimer();
    this.clearAdStopTimer();
    this.adPodQueue = [];
    this.adPodAllUrls = [];
    this.showAds = false;
    this.currentAdIndex = 0;
    this.totalAdsInBreak = 0;

    this.ima.stop().catch(() => {});

    const restoredMuted = this.ima.getOriginalMutedState();
    const restoredVolume = this.ima.getOriginalVolume();

    if (this.video.muted !== restoredMuted) {
      this.video.muted = restoredMuted;
    }
    if (Math.abs(this.video.volume - restoredVolume) > 0.01) {
      this.video.volume = restoredVolume;
    }

    if (!this.shouldContinueLiveStreamDuringAds() && this.video.paused) {
      this.video.play()?.catch(() => {});
    }
  }

  private handleAdFailure(): void {
    const remaining = this.getRemainingAdMs();
    
    if (this.config.debugAdTiming) {
      console.log(`[CONTINUOUS-FETCH] Ad failure: remaining=${remaining}ms, queued ads=${this.adRequestQueue.length}`);
    }
    
    if (remaining > 500) {
      this.tryNextAvailableAd();
    } else {
      console.error("[AD-ERROR] Ad failed and no time remaining. Failed URLs:", this.failedVastUrls.size);
      this.handleAdPodComplete();
    }
  }


  private startAdRequestWatchdog(token: number): void {
    this.clearAdRequestWatchdog();

    const timeoutMs = this.config.adFailsafeTimeoutMs ?? 10000;
    this.adRequestWatchdogToken = token;
    this.adRequestWatchdogId = window.setTimeout(() => {
      if (this.adRequestWatchdogToken !== token) {
        return;
      }

      this.adRequestWatchdogId = undefined;
      this.adRequestWatchdogToken = null;
      if (this.activeAdRequestToken === token) {
        this.activeAdRequestToken = null;
      }

      this.logAdState("ad_request_timeout", { token, timeoutMs });
      this.handleAdFailure();
    }, timeoutMs) as unknown as number;

    this.logAdState("ad_request_watchdog_started", { token, timeoutMs });
  }

  private clearAdRequestWatchdog(): void {
    if (this.adRequestWatchdogId != null) {
      clearTimeout(this.adRequestWatchdogId);
      this.adRequestWatchdogId = undefined;
    }

    if (this.adRequestWatchdogToken != null) {
      this.logAdState("ad_request_watchdog_cleared", {
        token: this.adRequestWatchdogToken,
      });
      this.adRequestWatchdogToken = null;
    }
  }

  private startAdFailsafeTimer(token: number): void {
    this.clearAdFailsafeTimer();

    const failsafeMs = this.config.adFailsafeTimeoutMs ?? 10000;
    this.adFailsafeToken = token;

    this.adFailsafeTimerId = window.setTimeout(() => {
      if (this.adFailsafeToken !== token) {
        return;
      }

      this.adFailsafeTimerId = undefined;
      this.adFailsafeToken = null;

      if (this.activeAdRequestToken === token) {
        this.activeAdRequestToken = null;
      }

      this.logAdState("ad_failsafe_triggered", {
        token,
        failsafeMs,
        videoPaused: this.video.paused,
        imaAdPlaying: this.ima.isAdPlaying(),
      });

      this.handleAdFailure();
    }, failsafeMs) as unknown as number;

    this.logAdState("ad_failsafe_started", { token, failsafeMs });
  }

  private clearAdFailsafeTimer(): void {
    if (this.adFailsafeTimerId != null) {
      clearTimeout(this.adFailsafeTimerId);
      this.logAdState("ad_failsafe_cleared", { token: this.adFailsafeToken });
      this.adFailsafeTimerId = undefined;
    }

    this.adFailsafeToken = null;
  }

  private selectVastTagsForBreak(b?: AdBreak): string[] | undefined {
    if (!b || !b.vastTagUrl) return undefined;
    if (b.vastTagUrl.includes(",")) {
      return b.vastTagUrl
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    return [b.vastTagUrl];
  }

  private logAdState(event: string, extra: Record<string, unknown> = {}): void {
    if (!this.config.debugAdTiming) {
      return;
    }

    console.log("[StormcloudVideoPlayer][AdState]", {
      event,
      timestamp: new Date().toISOString(),
      showAds: this.showAds,
      adPlaying: this.ima.isAdPlaying(),
      inAdBreak: this.inAdBreak,
      activeAdRequestToken: this.activeAdRequestToken,
      ...extra,
    });
  }

  private async fetchAndParseVastXml(vastTagUrl: string): Promise<string[]> {
    try {
      const response = await fetch(vastTagUrl, { mode: "cors" });
      if (!response.ok) {
        throw new Error(`Failed to fetch VAST: ${response.status}`);
      }
      const xmlText = await response.text();
      return this.extractMediaUrlsFromVast(xmlText);
    } catch (error) {
      if (this.config.debugAdTiming) {
        console.warn(
          `[StormcloudVideoPlayer] Failed to fetch/parse VAST XML: ${vastTagUrl}`,
          error
        );
      }
      return [];
    }
  }

  private extractMediaUrlsFromVast(xmlText: string): string[] {
    const mediaUrls: string[] = [];
    
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, "text/xml");
      
      const mediaFileElements = xmlDoc.querySelectorAll("MediaFile");
      
      for (const mediaFile of Array.from(mediaFileElements)) {
        const url = mediaFile.textContent?.trim();
        if (url) {
          const lowerUrl = url.toLowerCase();
          if (
            lowerUrl.endsWith(".mp4") ||
            lowerUrl.endsWith(".webm") ||
            lowerUrl.endsWith(".mov") ||
            lowerUrl.endsWith(".avi") ||
            lowerUrl.includes(".mp4?") ||
            lowerUrl.includes(".webm?") ||
            lowerUrl.includes("/mp4/") ||
            lowerUrl.includes("type=video")
          ) {
            mediaUrls.push(url);
          }
        }
      }
      
      if (this.config.debugAdTiming && mediaUrls.length > 0) {
        console.log(
          `[StormcloudVideoPlayer] Extracted ${mediaUrls.length} media URLs from VAST:`,
          mediaUrls
        );
      }
    } catch (error) {
      if (this.config.debugAdTiming) {
        console.warn(
          "[StormcloudVideoPlayer] Failed to parse VAST XML:",
          error
        );
      }
    }
    
    return mediaUrls;
  }

  private async fetchVastDuration(vastTagUrl: string): Promise<number | null> {
    try {
      const response = await fetch(vastTagUrl, { mode: "cors" });
      if (!response.ok) {
        if (this.config.debugAdTiming) {
          console.warn(
            `[ADAPTIVE-POD] Failed to fetch VAST: ${response.status}`
          );
        }
        return null;
      }
      
      const xmlText = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, "text/xml");
      
      const durationText = xmlDoc.querySelector("Duration")?.textContent;
      if (!durationText) {
        if (this.config.debugAdTiming) {
          console.warn("[ADAPTIVE-POD] No Duration element found in VAST");
        }
        return null;
      }
      
      const durationParts = durationText.split(":");
      const durationSeconds =
        parseInt(durationParts[0] || "0", 10) * 3600 +
        parseInt(durationParts[1] || "0", 10) * 60 +
        parseInt(durationParts[2] || "0", 10);
      
      return durationSeconds;
    } catch (error) {
      if (this.config.debugAdTiming) {
        console.warn(
          `[ADAPTIVE-POD] Error fetching VAST duration from ${vastTagUrl}:`,
          error
        );
      }
      return null;
    }
  }

  private calculateAdditionalAdsNeeded(): number {
    if (!this.isAdaptiveMode || this.targetAdBreakDurationMs === null) {
      return 0;
    }

    let totalFetchedDurationMs = 0;
    for (const duration of this.fetchedAdDurations.values()) {
      totalFetchedDurationMs += duration * 1000;
    }

    const fetchedCount = this.fetchedAdDurations.size;
    const averageDurationMs = fetchedCount > 0 
      ? totalFetchedDurationMs / fetchedCount 
      : 30 * 1000;

    const queuedButNotFetched = this.adPodAllUrls.length - fetchedCount;
    
    const estimatedQueuedDurationMs = queuedButNotFetched * averageDurationMs;
    const estimatedTotalDurationMs = totalFetchedDurationMs + estimatedQueuedDurationMs;

    const remainingTimeMs = this.targetAdBreakDurationMs - estimatedTotalDurationMs;

    if (remainingTimeMs <= 0) {
      if (this.config.debugAdTiming) {
        console.log(
          `[ADAPTIVE-POD] ✅ Target duration met: Fetched=${totalFetchedDurationMs}ms + Queued(${queuedButNotFetched} ads)=${estimatedQueuedDurationMs}ms = ${estimatedTotalDurationMs}ms / Target=${this.targetAdBreakDurationMs}ms`
        );
      }
      return 0;
    }

    const additionalAds = Math.ceil(remainingTimeMs / averageDurationMs);

    if (this.config.debugAdTiming) {
      console.log(
        `[ADAPTIVE-POD] 📊 Need ${additionalAds} more ads | ` +
        `Fetched: ${totalFetchedDurationMs}ms (${fetchedCount} ads) | ` +
        `Queued: ${estimatedQueuedDurationMs}ms (${queuedButNotFetched} ads) | ` +
        `Target: ${this.targetAdBreakDurationMs}ms | ` +
        `Remaining: ${remainingTimeMs}ms | ` +
        `Avg duration: ${averageDurationMs}ms`
      );
    }

    return additionalAds;
  }

  private async addAdaptiveAdsToQueue(): Promise<void> {
    if (!this.isAdaptiveMode || !this.apiVastTagUrl) {
      return;
    }

    const additionalAds = this.calculateAdditionalAdsNeeded();
    if (additionalAds <= 0) {
      return;
    }

    const newUrls = this.generateVastUrlsWithCorrelators(
      this.apiVastTagUrl,
      additionalAds
    );

    if (this.config.debugAdTiming) {
      console.log(
        `[ADAPTIVE-POD] 🔄 Adding ${newUrls.length} additional VAST URLs to queue (will be preloaded sequentially)`
      );
    }

    this.adPodAllUrls.push(...newUrls);
    this.adPodQueue.push(...newUrls);
    this.totalAdsInBreak += newUrls.length;
  }

  private async preloadMediaFile(mediaUrl: string): Promise<void> {
    if (this.preloadedMediaUrls.has(mediaUrl)) {
      return;
    }

    if (this.preloadingMediaUrls.has(mediaUrl)) {
      return;
    }

    this.preloadingMediaUrls.add(mediaUrl);

    try {
      if (this.config.debugAdTiming) {
        console.log(
          `[StormcloudVideoPlayer] Preloading video file: ${mediaUrl}`
        );
      }

      const response = await fetch(mediaUrl, {
        mode: "cors",
        method: "GET",
        headers: {
          Range: "bytes=0-1048576",
        },
      });

      if (response.ok || response.status === 206) {
        this.preloadedMediaUrls.add(mediaUrl);
        if (this.config.debugAdTiming) {
          console.log(
            `[StormcloudVideoPlayer] Successfully preloaded video file: ${mediaUrl}`
          );
        }
      }
    } catch (error) {
      if (this.config.debugAdTiming) {
        console.warn(
          `[StormcloudVideoPlayer] Failed to preload video file: ${mediaUrl}`,
          error
        );
      }
    } finally {
      this.preloadingMediaUrls.delete(mediaUrl);
    }
  }

  private async preloadAllAdsInBackground(): Promise<void> {
    if (this.adPodAllUrls.length === 0) {
      return;
    }

    if (this.isAdaptiveMode) {
      if (this.config.debugAdTiming) {
        console.log(
          `[ADAPTIVE-POD] 🔄 Starting sequential preload of remaining ads`
        );
      }

      const processedUrls = new Set<string>();
      
      for (const url of this.adPodAllUrls) {
        if (this.ima.hasPreloadedAd?.(url) || this.fetchedAdDurations.has(url)) {
          processedUrls.add(url);
        }
      }

      if (this.config.debugAdTiming && processedUrls.size > 0) {
        console.log(
          `[ADAPTIVE-POD] 📦 Skipping ${processedUrls.size} already-preloaded ads`
        );
      }

      while (true) {
        const nextUrl = this.adPodAllUrls.find(url => !processedUrls.has(url));
        
        if (!nextUrl) {
          if (this.config.debugAdTiming) {
            console.log(`[ADAPTIVE-POD] ✅ All queued ads processed (${processedUrls.size} total)`);
          }
          break;
        }

        processedUrls.add(nextUrl);

        if (this.config.debugAdTiming) {
          console.log(
            `[ADAPTIVE-POD] 📥 Preloading ad ${processedUrls.size}/${this.adPodAllUrls.length}...`
          );
        }

        try {
          await this.preloadSingleAd(nextUrl);
        } catch (error) {
          if (this.config.debugAdTiming) {
            console.warn(
              `[ADAPTIVE-POD] ⚠️ Preload failed for ad ${processedUrls.size}:`,
              error
            );
          }
        }

        if (this.calculateAdditionalAdsNeeded() === 0) {
          if (this.config.debugAdTiming) {
            console.log(
              `[ADAPTIVE-POD] ✅ Target duration reached (${processedUrls.size} ads preloaded), stopping`
            );
          }
          break;
        }
      }

      if (this.config.debugAdTiming) {
        console.log(
          `[ADAPTIVE-POD] ✅ Sequential preloading completed (${processedUrls.size} ads ready)`
        );
      }
    } else {
      if (this.config.debugAdTiming) {
        console.log(
          `[StormcloudVideoPlayer] Starting parallel preload of ${this.adPodAllUrls.length} ads`
        );
      }

      const preloadPromises = this.adPodAllUrls.map((vastTagUrl) =>
        this.preloadSingleAd(vastTagUrl).catch((error) => {
          if (this.config.debugAdTiming) {
            console.warn(
              `[StormcloudVideoPlayer] Preload failed for ${vastTagUrl}:`,
              error
            );
          }
        })
      );

      await Promise.all(preloadPromises);

      if (this.config.debugAdTiming) {
        console.log(
          `[StormcloudVideoPlayer] Background preloading completed for all ads`
        );
      }
    }
  }

  private async preloadSingleAd(vastTagUrl: string): Promise<void> {
    if (!vastTagUrl) return;

    try {
      if (this.isAdaptiveMode && !this.fetchedAdDurations.has(vastTagUrl)) {
        const duration = await this.fetchVastDuration(vastTagUrl);
        if (duration !== null) {
          this.fetchedAdDurations.set(vastTagUrl, duration);
          
          if (this.config.debugAdTiming) {
            console.log(
              `[ADAPTIVE-POD] ✓ Fetched ad duration: ${duration}s (${this.fetchedAdDurations.size} ads fetched so far)`
            );
          }

          await this.addAdaptiveAdsToQueue();
        }
      }

      if (this.ima.preloadAds && !this.ima.hasPreloadedAd(vastTagUrl)) {
        if (!this.preloadingAdUrls.has(vastTagUrl)) {
          if (this.config.debugAdTiming) {
            console.log(
              `[StormcloudVideoPlayer] Preloading VAST: ${vastTagUrl}`
            );
          }

          this.preloadingAdUrls.add(vastTagUrl);

          await this.ima
            .preloadAds(vastTagUrl)
            .then(() => {
              if (this.config.debugAdTiming) {
                console.log(
                  `[StormcloudVideoPlayer] IMA VAST preload complete: ${vastTagUrl}`
                );
              }
            })
            .catch((error) => {
              if (this.config.debugAdTiming) {
                console.warn(
                  `[StormcloudVideoPlayer] IMA VAST preload failed: ${vastTagUrl}`,
                  error
                );
              }
            })
            .finally(() => {
              this.preloadingAdUrls.delete(vastTagUrl);
            });
        }
      }

      let mediaUrls = this.vastToMediaUrlMap.get(vastTagUrl);

      if (!mediaUrls) {
        if (this.config.debugAdTiming) {
          console.log(
            `[StormcloudVideoPlayer] Fetching and parsing VAST to extract media URLs: ${vastTagUrl}`
          );
        }

        mediaUrls = await this.fetchAndParseVastXml(vastTagUrl);

        if (this.config.debugAdTiming) {
          console.log(
            `[StormcloudVideoPlayer] Extracted ${mediaUrls.length} media URLs:`,
            mediaUrls
          );
        }

        if (mediaUrls.length > 0) {
          this.vastToMediaUrlMap.set(vastTagUrl, mediaUrls);
        }
      }

      if (mediaUrls && mediaUrls.length > 0) {
        const primaryMediaUrl = mediaUrls[0];
        if (primaryMediaUrl && !this.preloadedMediaUrls.has(primaryMediaUrl)) {
          await this.preloadMediaFile(primaryMediaUrl);
        }
      }
    } catch (error) {
      if (this.config.debugAdTiming) {
        console.warn(
          `[StormcloudVideoPlayer] Failed to preload ad: ${vastTagUrl}`,
          error
        );
      }
    }
  }

  private findNextPreloadedAd(): string | undefined {
    for (let i = 0; i < this.adPodQueue.length; i++) {
      const vastTagUrl = this.adPodQueue[i];
      if (!vastTagUrl) continue;

      if (this.failedVastUrls.has(vastTagUrl)) {
        console.warn("[AD-ERROR] Skipping failed URL in queue");
        continue;
      }

      const hasImaPreload = this.ima.hasPreloadedAd?.(vastTagUrl) ?? false;
      const mediaUrls = this.vastToMediaUrlMap.get(vastTagUrl);
      const hasMediaPreload =
        mediaUrls && mediaUrls.length > 0
          ? this.preloadedMediaUrls.has(mediaUrls[0]!)
          : false;

      if (hasImaPreload || hasMediaPreload) {
        this.adPodQueue.splice(0, i + 1);
        return vastTagUrl;
      }
    }

    return undefined;
  }

  private getRemainingAdMs(): number {
    if (
      this.expectedAdBreakDurationMs == null ||
      this.currentAdBreakStartWallClockMs == null
    )
      return 0;
    const elapsed = Date.now() - this.currentAdBreakStartWallClockMs;
    return Math.max(0, this.expectedAdBreakDurationMs - elapsed);
  }

  private findBreakForTime(nowMs: number): AdBreak | undefined {
    const schedule: AdBreak[] = [];
    for (const b of schedule) {
      const end = (b.startTimeMs || 0) + (b.durationMs || 0);
      if (
        nowMs >= (b.startTimeMs || 0) &&
        (b.durationMs ? nowMs < end : true)
      ) {
        return b;
      }
    }
    return undefined;
  }

  toggleMute(): void {
    if (this.ima.isAdPlaying()) {
      const currentPerceptualState = this.isMuted();
      const newMutedState = !currentPerceptualState;

      this.ima.updateOriginalMutedState(newMutedState, this.video.volume);
      this.ima.setAdVolume(newMutedState ? 0 : 1);

      if (this.config.debugAdTiming) {
        console.log(
          "[StormcloudVideoPlayer] Mute toggle during ad - immediately applied:",
          newMutedState
        );
      }
    } else {
      this.video.muted = !this.video.muted;
      this.ima.updateOriginalMutedState(this.video.muted, this.video.volume);
      if (this.config.debugAdTiming) {
        console.log("[StormcloudVideoPlayer] Muted:", this.video.muted);
      }
    }
  }

  toggleFullscreen(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!document.fullscreenElement) {
        const container = this.video.parentElement;
        if (!container) {
          reject(new Error("No parent container found for fullscreen"));
          return;
        }
        container
          .requestFullscreen()
          .then(() => {
            if (this.config.debugAdTiming) {
              console.log("[StormcloudVideoPlayer] Entered fullscreen");
            }
            resolve();
          })
          .catch((err) => {
            if (this.config.debugAdTiming) {
              console.error("[StormcloudVideoPlayer] Fullscreen error:", err);
            }
            reject(err);
          });
      } else {
        document
          .exitFullscreen()
          .then(() => {
            if (this.config.debugAdTiming) {
              console.log("[StormcloudVideoPlayer] Exited fullscreen");
            }
            resolve();
          })
          .catch((err) => {
            if (this.config.debugAdTiming) {
              console.error(
                "[StormcloudVideoPlayer] Exit fullscreen error:",
                err
              );
            }
            reject(err);
          });
      }
    });
  }

  isMuted(): boolean {
    if (this.ima.isAdPlaying()) {
      if (this.config.debugAdTiming) {
        console.log(
          "[StormcloudVideoPlayer] isMuted() override during ad playback -> false"
        );
      }
      return false;
    }
    return this.video.muted;
  }

  setMuted(muted: boolean): void {
    const adPlaying = this.ima.isAdPlaying();

    if (adPlaying && muted === this.video.muted) {
      if (this.config.debugAdTiming) {
        console.log(
          "[StormcloudVideoPlayer] setMuted reflective update during ad ignored",
          { muted }
        );
      }
      return;
    }

    this.video.muted = muted;

    if (adPlaying) {
      this.ima.updateOriginalMutedState(muted, this.video.volume);
      this.ima.setAdVolume(muted ? 0 : 1);
      if (this.config.debugAdTiming) {
        console.log("[StormcloudVideoPlayer] setMuted applied during ad", {
          muted,
        });
      }
      return;
    }

    this.ima.updateOriginalMutedState(muted, this.video.volume);
    if (this.config.debugAdTiming) {
      console.log("[StormcloudVideoPlayer] setMuted called:", muted);
    }
  }

  setVolume(volume: number): void {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    const adPlaying = this.ima.isAdPlaying();

    if (adPlaying) {
      this.ima.setAdVolume(clampedVolume);
      this.ima.updateOriginalMutedState(clampedVolume === 0, clampedVolume);
      if (this.config.debugAdTiming) {
        console.log("[StormcloudVideoPlayer] setVolume applied during ad", {
          volume: clampedVolume,
        });
      }
    } else {
      this.video.volume = clampedVolume;
      this.video.muted = clampedVolume === 0;
      this.ima.updateOriginalMutedState(clampedVolume === 0, clampedVolume);
      if (this.config.debugAdTiming) {
        console.log("[StormcloudVideoPlayer] setVolume called:", clampedVolume);
      }
    }
  }

  isFullscreen(): boolean {
    return !!document.fullscreenElement;
  }

  isLive(): boolean {
    return this.isLiveStream;
  }

  get videoElement(): HTMLVideoElement {
    return this.video;
  }

  resize(): void {
    if (this.config.debugAdTiming) {
      console.log("[StormcloudVideoPlayer] Resizing player");
    }

    if (this.ima && this.ima.isAdPlaying()) {
      const width = this.video.clientWidth || 640;
      const height = this.video.clientHeight || 360;

      if (this.config.debugAdTiming) {
        console.log(
          `[StormcloudVideoPlayer] Resizing ads manager to ${width}x${height}`
        );
      }

      this.ima.resize(width, height);
    }
  }

  destroy(): void {
    this.stopContinuousFetching();
    this.clearAdStartTimer();
    this.clearAdStopTimer();
    this.clearAdFailsafeTimer();
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = undefined;
    }
    this.hls?.destroy();
    this.ima?.destroy();
    this.preloadingAdUrls.clear();
    this.vastToMediaUrlMap.clear();
    this.preloadedMediaUrls.clear();
    this.preloadingMediaUrls.clear();
    this.adPodAllUrls = [];
    this.adRequestQueue = [];
    this.successfulAdRequests = [];
  }
}
